import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { DASHBOARD } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Users, UserCircle, Package, FileText, Calendar, Building2, LogOut, Menu, Activity, Shield, ScrollText, User as UserIcon, UserPlus, Globe, BookOpen, Trash2 } from 'lucide-react';

const DashboardLayout = () => {
  const { user, logout, markManualSeen } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const welcomeTriggered = useRef(false);

  // Onboarding: show manual automatically the first time a new Admin logs in
  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin' && user.has_seen_manual !== true && !welcomeTriggered.current) {
      welcomeTriggered.current = true;
      navigate('/dashboard/manual?welcome=1');
      markManualSeen();
    }
  }, [user, navigate, markManualSeen]);

  const handleLogout = async () => {
    await logout();
    navigate('/auth');
  };

  const menuItems = [
    { path: '/dashboard', icon: FileText, label: 'Inicio', testId: DASHBOARD.navReports },
    { path: '/dashboard/patients', icon: Users, label: 'Pacientes', testId: DASHBOARD.navPatients },
    { path: '/dashboard/therapists', icon: UserCircle, label: 'Terapeutas', testId: DASHBOARD.navTherapists },
    { path: '/dashboard/resources', icon: Package, label: 'Recursos', testId: DASHBOARD.navResources },
    { path: '/dashboard/reports', icon: FileText, label: 'Informes', testId: DASHBOARD.navReports },
    { path: '/dashboard/appointments', icon: Calendar, label: 'Calendario', testId: DASHBOARD.navAppointments },
    { path: '/dashboard/analytics', icon: Activity, label: 'Analytics', testId: 'dashboard-nav-analytics' },
    { path: '/dashboard/institution', icon: Building2, label: 'Institución', testId: DASHBOARD.navInstitution },
  ];
  
  // Add users management for admin and super_admin only
  const canManageUsers = user?.role === 'admin' || user?.role === 'super_admin';
  const isSuperAdmin = user?.role === 'super_admin';
  
  if (canManageUsers) {
    menuItems.push({
      path: '/dashboard/users',
      icon: Shield,
      label: 'Usuarios',
      testId: 'dashboard-nav-users'
    });
    menuItems.push({
      path: '/dashboard/admin-panel',
      icon: UserPlus,
      label: 'Admin Panel',
      testId: 'dashboard-nav-admin-panel'
    });
    menuItems.push({
      path: '/dashboard/audit-logs',
      icon: ScrollText,
      label: 'Auditoría',
      testId: 'dashboard-nav-audit'
    });
  }
  
  // Add global panel for super_admin only
  if (isSuperAdmin) {
    menuItems.push({
      path: '/dashboard/global-panel',
      icon: Globe,
      label: 'Panel Global',
      testId: 'dashboard-nav-global-panel'
    });
  }
  
  // Add profile for all users
  menuItems.push({
    path: '/dashboard/profile',
    icon: UserIcon,
    label: 'Mi Perfil',
    testId: 'dashboard-nav-profile'
  });

  // Manual / Ayuda for all users
  menuItems.push({
    path: '/dashboard/manual',
    icon: BookOpen,
    label: 'Ayuda / Manual',
    testId: 'dashboard-nav-manual'
  });

  // Central recycle bin for roles that can delete
  if (['admin', 'super_admin', 'therapist'].includes(user?.role)) {
    menuItems.push({
      path: '/dashboard/trash',
      icon: Trash2,
      label: 'Papelera',
      testId: 'dashboard-nav-trash'
    });
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F9F8F6' }} data-testid={DASHBOARD.container}>
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block fixed md:relative inset-0 z-50 md:z-auto`}>
        <div className="h-full w-64 bg-white border-r border-[#E5E2DC] flex flex-col">
          <div className="p-6 border-b border-[#E5E2DC]">
            <h2 className="text-xl font-semibold" style={{ color: '#5A7D6C', fontFamily: 'Manrope' }}>
              REKORD
            </h2>
            <p className="text-sm mt-1" style={{ color: '#718096' }}>
              {user?.institution_name}
            </p>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={item.testId}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#5A7D6C] text-white'
                      : 'text-[#2D3748] hover:bg-[#E5E2DC]'
                  }`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-[#E5E2DC]">
            <div className="mb-3">
              <p className="text-sm font-medium" style={{ color: '#2D3748' }}>
                {user?.name}
              </p>
              <p className="text-xs" style={{ color: '#718096' }}>
                {user?.email}
              </p>
            </div>
            <Button
              onClick={handleLogout}
              data-testid={DASHBOARD.logoutBtn}
              variant="outline"
              className="w-full flex items-center gap-2"
            >
              <LogOut size={16} />
              Cerrar Sesión
            </Button>
          </div>
        </div>
        {sidebarOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/50 -z-10"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white border-b border-[#E5E2DC] p-4 md:hidden">
          <Button variant="ghost" onClick={() => setSidebarOpen(true)}>
            <Menu />
          </Button>
        </header>

        <main className="flex-1 p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
